import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  isServiceWorkerReady: boolean;
  canInstall: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
}

export function usePWA() {
  const [pwaState, setPwaState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: false,
    isOnline: navigator.onLine,
    isServiceWorkerReady: false,
    canInstall: false,
    deferredPrompt: null,
  });

  // Vérifier si l'app est déjà installée
  const checkIfInstalled = useCallback(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInWebAppiOS = (window.navigator as any).standalone === true;

    return isStandalone || isInWebAppiOS;
  }, []);

  // Enregistrer le Service Worker
  const registerServiceWorker = useCallback(async () => {
    // En mode développement, enregistrer le service worker pour permettre les push notifications
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });

        console.log('[PWA] Service Worker registered:', registration);

        // Vérifier les mises à jour
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Nouvelle version disponible
                console.log('[PWA] New version available');
                // Émettre un événement pour notifier l'utilisateur
                window.dispatchEvent(new CustomEvent('sw-update-available'));
              }
            });
          }
        });

        setPwaState(prev => ({ ...prev, isServiceWorkerReady: true }));
        return registration;
      } catch (error) {
        console.error('[PWA] Service Worker registration failed:', error);
      }
    }
    return null;
  }, []);

  // Gérer l'événement beforeinstallprompt
  const handleBeforeInstallPrompt = useCallback((event: Event) => {
    event.preventDefault();
    const promptEvent = event as BeforeInstallPromptEvent;

    setPwaState(prev => ({
      ...prev,
      canInstall: true,
      deferredPrompt: promptEvent,
    }));
  }, []);

  // Gérer l'événement appinstalled
  const handleAppInstalled = useCallback(() => {
    console.log('[PWA] App installed');
    setPwaState(prev => ({
      ...prev,
      isInstalled: true,
      canInstall: false,
      deferredPrompt: null,
    }));
  }, []);

  // Gérer les changements de connexion
  const handleOnlineStatus = useCallback(() => {
    setPwaState(prev => ({ ...prev, isOnline: navigator.onLine }));
  }, []);

  // Installer l'application
  const installApp = useCallback(async () => {
    if (pwaState.deferredPrompt) {
      pwaState.deferredPrompt.prompt();

      try {
        const choiceResult = await pwaState.deferredPrompt.userChoice;
        console.log('[PWA] User choice:', choiceResult.outcome);

        setPwaState(prev => ({
          ...prev,
          canInstall: false,
          deferredPrompt: null,
        }));

        return choiceResult.outcome === 'accepted';
      } catch (error) {
        console.error('[PWA] Install prompt failed:', error);
        return false;
      }
    }
    return false;
  }, [pwaState.deferredPrompt]);

  // Mettre à jour le Service Worker
  const updateServiceWorker = useCallback(async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.location.reload();
      }
    }
  }, []);

  // Vider le cache
  const clearCache = useCallback(async () => {
    if ('serviceWorker' in navigator && 'caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('[PWA] Cache cleared');
        return true;
      } catch (error) {
        console.error('[PWA] Failed to clear cache:', error);
        return false;
      }
    }
    return false;
  }, []);

  // Obtenir les informations du cache
  const getCacheInfo = useCallback(async () => {
    if ('serviceWorker' in navigator && 'caches' in window) {
      try {
        const cacheNames = await caches.keys();
        const cacheInfo = await Promise.all(
          cacheNames.map(async (cacheName) => {
            const cache = await caches.open(cacheName);
            const keys = await cache.keys();
            return {
              name: cacheName,
              size: keys.length,
              urls: keys.map(req => req.url),
            };
          })
        );
        return cacheInfo;
      } catch (error) {
        console.error('[PWA] Failed to get cache info:', error);
        return [];
      }
    }
    return [];
  }, []);

  // Effet pour initialiser la PWA
  useEffect(() => {
    // Vérifier l'état d'installation
    const installed = checkIfInstalled();
    setPwaState(prev => ({ ...prev, isInstalled: installed }));

    // Enregistrer le Service Worker
    registerServiceWorker();

    // Écouter les événements PWA
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Nettoyer les écouteurs
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, [
    checkIfInstalled,
    registerServiceWorker,
    handleBeforeInstallPrompt,
    handleAppInstalled,
    handleOnlineStatus,
  ]);

  return {
    ...pwaState,
    installApp,
    updateServiceWorker,
    clearCache,
    getCacheInfo,
  };
}